import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { fetchResumeData, fetchPublications } from "../../fetchProjects";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import ProfessionalSummary from "./components/ProfessionalSummary";
import CoreCompetencies from "./components/CoreCompetencies";
import WorkExperience from "./components/WorkExperience";
import Education from "./components/Education";
import NotableAchievements from "./components/NotableAchievements";
import DownloadSection from "./components/DownloadSection";
import References from "./components/References";
import ResearchPublications from "./components/ResearchPublications";
import ResumePrintLayout from "./components/ResumePrintLayout";
import Icon from "../../components/AppIcon";
import { useGlobalContext } from "../../context/GlobalContext";

const ResumePage = () => {
  const { globalSettings: settings } = useGlobalContext();
  const [resumeItems, setResumeItems] = useState([]);
  const [publications, setPublications] = useState([]);
  const [overviewBullets, setOverviewBullets] = useState([
    "NARL SRFP Intern",
    "NASA STEM Tutor",
    "ISRO UAV Remote Sensing",
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const loadData = async () => {
      try {
        const [data, pubs] = await Promise.all([
          fetchResumeData(),
          fetchPublications()
        ]);
        setResumeItems(data || []);
        setPublications(pubs || []);
        
        const experiences = (data || []).filter((item) => item.type === "experience");
        if (experiences.length >= 3) {
          setOverviewBullets(experiences.slice(0, 3).map((exp) => exp.title));
        } else if (experiences.length > 0) {
          const fetched = experiences.map((exp) => exp.title);
          const initial = [
            "NARL SRFP Intern",
            "NASA STEM Tutor",
            "ISRO UAV Remote Sensing",
          ];
          setOverviewBullets([...fetched, ...initial.slice(fetched.length)]);
        }
      } catch (err) {
        console.error("Failed to load resume data", err);
      }
    };

    loadData();
  }, []);

  // Generate dynamic JSON-LD from resumeItems
  const educationItems = resumeItems.filter(item => item.type === 'education').map(item => ({
    "@type": "EducationalOccupationalCredential",
    "name": item.title,
    "abstract": item.description,
    "credentialCategory": "Degree",
    "recognizedBy": {
      "@type": "Organization",
      "name": item.organization
    }
  }));

  const experienceItems = resumeItems.filter(item => item.type === 'experience').map(item => {
    const rawDate = item.date_value || item.duration || item.date || "";
    const splitDate = rawDate.includes('-') ? rawDate.split('-') : [rawDate, "Present"];
    
    return {
      "@type": "WorkBasedProgram",
      "name": item.title,
      "occupationalCategory": "Technology",
      "provider": {
        "@type": "Organization",
        "name": item.organization
      },
      "startDate": splitDate[0]?.trim() || "",
      "endDate": splitDate[1]?.trim() || "Present"
    };
  });

  const certificationItems = resumeItems.filter(item => item.type === 'certification').map(item => ({
    "@type": "EducationalOccupationalCredential",
    "name": item.title,
    "credentialCategory": "Certificate",
    "recognizedBy": {
      "@type": "Organization",
      "name": item.organization
    }
  }));

  const dynamicHasCredential = [...educationItems, ...experienceItems, ...certificationItems].slice(0, 15);

  const seoSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": "https://sudhan.live/resume#webpage",
        "url": "https://sudhan.live/resume",
        "name": "Resume - Sudhan R",
        "description": "Comprehensive professional resume, educational milestones, and research history of Sudhan R.",
        "isPartOf": { "@id": "https://sudhan.live/#website" },
        "mainEntity": { "@id": "https://sudhan.live/#person" },
        "breadcrumb": { "@id": "https://sudhan.live/resume#breadcrumb" }
      },
      {
        "@type": "Person",
        "@id": "https://sudhan.live/#person",
        "name": "Sudhan R",
        "alternateName": "Sudhan-23082004",
        "image": "https://sudhan.live/assets/images/Sudhan_2026_v3.png",
        "jobTitle": "Computer Science Engineer & Space Researcher",
        "url": "https://sudhan.live/",
        "sameAs": [
          "https://www.linkedin.com/in/sudhan23082004/",
          "https://github.com/Sudhan-23082004",
          "https://orcid.org/0009-0007-0461-2150"
        ],
        "knowsAbout": [
          "Artificial Intelligence", "Machine Learning", "Deep Learning", "Python", "Java", "C++", "JavaScript", "TypeScript", "React", "Node.js", "Cloudflare", "GIS", "Remote Sensing", "Earth Observation", "Satellite Data Processing", "UAV Data Processing", "Drone Mapping", "Geospatial Analytics", "Computer Vision", "Image Processing", "Scientific Computing", "Data Science", "Data Visualization", "Cloud Computing", "Software Engineering", "Full Stack Development", "Open Source Software", "Research"
        ],
        "hasCredential": dynamicHasCredential.length > 0 ? dynamicHasCredential : [
          {
            "@type": "EducationalOccupationalCredential",
            "name": "UAV Remote Sensing – ISRO NESAC"
          }
        ]
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://sudhan.live/resume#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://sudhan.live/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Resume",
            "item": "https://sudhan.live/resume"
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-transparent text-text-primary print:bg-white print:text-black">
      <Helmet>
        <title>
          Resume – Sudhan R | Computer Science Engineer & Space Researcher
        </title>
        <meta
          name="description"
          content="Explore the detailed professional resume of Sudhan R – ISRO-NARL Summer Research Fellow, NASA STEM Gateway Tutor, UAV Remote Sensing trainee, and space-tech innovator. Specializing in AI, geospatial data, and software development."
        />
        <meta
          name="keywords"
          content="Sudhan R resume, ISRO intern, NASA STEM tutor, NARL, remote sensing, UAV, NDVI, NDRE, LIDAR, AI, machine learning, data science, atmospheric science, space researcher, research fellow, Computer Science Engineer"
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Resume – Sudhan R | Computer Science Engineer & Space Researcher"
        />
        <meta
          property="og:description"
          content="Computer Science Engineer and Space Researcher with experience in remote sensing, machine learning, and space science. Worked with ISRO, NASA, and IAAC."
        />
        <meta property="og:type" content="profile" />
        <meta
          property="og:url"
          content="https://sudhan.live/resume"
        />
        <meta
          property="og:image"
          content="https://sudhan.live/assets/images/Sudhan_2026_v3.png"
        />
        <meta property="og:locale" content="en_IN" />
        <meta name="author" content="Sudhan R" />
        <link
          rel="canonical"
          href="https://sudhan.live/resume"
        />

        {/* JSON-LD: Resume + Person + Educational Background + Skills */}
        <script type="application/ld+json">
          {JSON.stringify(seoSchema)}
        </script>
      </Helmet>
      <div className="print:hidden w-full">
        <div className="print:hidden">
          <Header />
        </div>

        <main className="pt-20 lg:pt-24 print:pt-0">
          {/* Hero Section */}
          <section className="bg-background relative overflow-hidden py-16 lg:py-20 border-b border-white/10 print:hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 opacity-50"></div>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
              <h1 className="font-heading font-bold text-4xl text-text-primary lg:text-5xl xl:text-6xl mb-4">
                Resume Overview
              </h1>
              <p className="text-xl lg:text-2xl text-text-secondary mb-6 max-w-3xl mx-auto">
                Exploring space science through research, education, and
                innovation. Driven by curiosity. Guided by real-world experience.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-text-muted">
                {overviewBullets.map((bullet, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-accent rounded-full shadow-[0_0_8px_rgba(14,165,233,0.5)]"></div>
                    <span>{bullet}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Resume Content */}
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 print:py-0 print:px-0 print:max-w-none print:w-full">
            <div className="flex justify-end mb-4 print:hidden">
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2 rounded-lg text-text-primary text-sm font-semibold transition-smooth shadow-sm hover:border-accent/40 group"
              >
                <Icon name="Printer" size={16} className="text-text-secondary group-hover:text-accent transition-smooth" />
                Print Resume
              </button>
            </div>
            <ProfessionalSummary />
            <CoreCompetencies />
            <WorkExperience />
            <Education />
            <ResearchPublications />
            <NotableAchievements />
            <DownloadSection />
            <References />
          </div>

          {/* Call to Action Section */}
          <section className="bg-background relative overflow-hidden py-16 lg:py-20 pb-24 print:hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 opacity-50"></div>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 lg:p-12 shadow-2xl">
              <h2 className="font-heading font-bold text-3xl lg:text-4xl text-text-primary mb-4">
                Curious to Collaborate?
              </h2>
              <p className="text-xl text-text-secondary mb-8 max-w-2xl mx-auto">
                Let’s talk about research, outreach, or collaborative STEM
                innovation. I’m open to new opportunities and missions that make
                an impact.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <a
                  href="/contact"
                  className="bg-accent text-white hover:bg-accent-600 font-heading font-semibold px-8 py-3 rounded-lg transition-smooth shadow-lg hover:shadow-accent/40 transform hover:-translate-y-0.5"
                >
                  Get In Touch
                </a>
                <a
                  href="/projects"
                  className="border-2 border-white/20 text-text-primary hover:bg-white/10 font-heading font-semibold px-8 py-3 rounded-lg transition-smooth"
                >
                  View My Work
                </a>
              </div>
            </div>
          </section>
        </main>

      <div className="print:hidden">
        <Footer />
      </div>
    </div>

      <ResumePrintLayout resumeItems={resumeItems} publications={publications} settings={settings} />
    </div>
  );
};

export default ResumePage;
